import {
  DEFAULT_BASIC_PIECES,
  GAMEPLAY_TYPES,
  normalizePieceType
} from "./pieceCatalog";

export const LEVEL_SCHEMA_VERSION = "cg-level-v1";
export const LEVEL_BUNDLE_SCHEMA_VERSION = "cg-level-bundle-v1";

function toInt(value, fallback) {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function toFloat(value, fallback) {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function toBool(value, fallback = false) {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") {
    if (value === "true") return true;
    if (value === "false") return false;
  }
  return fallback;
}

function toVec2(raw, fallbackX = -1, fallbackY = -1) {
  if (!raw || typeof raw !== "object") {
    return { x: fallbackX, y: fallbackY };
  }
  return {
    x: toInt(raw.x, fallbackX),
    y: toInt(raw.y, fallbackY)
  };
}

function normalizePieceList(rawList) {
  if (!Array.isArray(rawList)) return [];
  return rawList
    .map((piece) => normalizePieceType(piece, "Empty"))
    .filter((piece) => piece !== "Empty");
}

function normalizePieceTypeOrId(value, fallback = "Empty") {
  if (typeof value === "number" && Number.isFinite(value)) {
    return toInt(value, 0);
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (/^-?\d+$/.test(trimmed)) {
      return toInt(trimmed, 0);
    }
    return normalizePieceType(trimmed, fallback);
  }

  return normalizePieceType(value, fallback);
}

function normalizePrefabReference(rawPrefab) {
  if (!rawPrefab || typeof rawPrefab !== "object") return null;

  const guid = typeof rawPrefab.guid === "string" ? rawPrefab.guid.trim() : "";
  if (!guid) return null;

  let fileID = "0";
  if (typeof rawPrefab.fileID === "string" && /^-?\d+$/.test(rawPrefab.fileID.trim())) {
    fileID = rawPrefab.fileID.trim();
  } else if (typeof rawPrefab.fileID === "number" && Number.isFinite(rawPrefab.fileID)) {
    fileID = String(Math.trunc(rawPrefab.fileID));
  } else if (typeof rawPrefab.fileID === "bigint") {
    fileID = rawPrefab.fileID.toString();
  }

  return {
    fileID,
    guid,
    type: toInt(rawPrefab.type, 3)
  };
}

function normalizePrefabReferences(rawPrefabs) {
  if (!Array.isArray(rawPrefabs)) return [];
  return rawPrefabs.map(normalizePrefabReference).filter(Boolean);
}

function normalizeVector2List(rawList, fallbackX = -1, fallbackY = -1) {
  if (!Array.isArray(rawList)) return [];
  return rawList.map((entry) => toVec2(entry, fallbackX, fallbackY));
}

function normalizeFirstWishForcedTargetsByColor(rawList) {
  if (!Array.isArray(rawList)) return [];
  return rawList.map((entry) => ({
    genieColor: toInt(entry?.genieColor, 0),
    cells: normalizeVector2List(entry?.cells, -1, -1)
  }));
}

function normalizeFirstWishForcedTargets(rawList) {
  if (!Array.isArray(rawList)) return [];
  return rawList.map((entry) => ({
    genieId:
      typeof entry?.genieId === "string" && entry.genieId.trim().length > 0
        ? entry.genieId.trim()
        : "",
    cell: toVec2(entry?.cell, -1, -1)
  }));
}

function normalizeForcedMoveTileOverrides(rawList) {
  if (!Array.isArray(rawList)) return [];
  return rawList.map((entry) => ({
    cell: toVec2(entry?.cell, -1, -1),
    pieceType: normalizePieceTypeOrId(entry?.pieceType, "Empty")
  }));
}

function normalizeForcedMoveGenieOverrides(rawList) {
  if (!Array.isArray(rawList)) return [];
  return rawList.map((entry) => ({
    genieColor: toInt(entry?.genieColor, 0),
    tileOverrides: normalizeForcedMoveTileOverrides(entry?.tileOverrides)
  }));
}

function normalizeForcedMoveSequence(rawList) {
  if (!Array.isArray(rawList)) return [];
  return rawList.map((entry) => ({
    activeTiles: normalizeVector2List(entry?.activeTiles, -1, -1),
    visibleTiles: normalizeVector2List(entry?.visibleTiles, -1, -1),
    defaultOverrides: normalizeForcedMoveTileOverrides(entry?.defaultOverrides),
    genieColorOverrides: normalizeForcedMoveGenieOverrides(entry?.genieColorOverrides)
  }));
}

function normalizeGenieColorPieceSwaps(rawList) {
  if (!Array.isArray(rawList)) return [];
  return rawList.map((entry) => ({
    genieColor: toInt(entry?.genieColor, 0),
    swaps: Array.isArray(entry?.swaps)
      ? entry.swaps.map((swap) => ({
          from: normalizePieceTypeOrId(swap?.from, "Empty"),
          to: normalizePieceTypeOrId(swap?.to, "Empty")
        }))
      : []
  }));
}

export function createEmptyPiece(collumn, line) {
  return {
    collumn,
    line,
    type: "NoElement",
    backgroundType: "Empty",
    foregroundType: "Empty",
    parent: { x: line, y: collumn },
    spawnerData: {
      spawnablePieces: [],
      sequentialPieces: [],
      quantity: 0
    },
    pieceColor: "Empty",
    startImune: false,
    isReceiver: false,
    linkedTeleporter: { x: -1, y: -1 },
    stretcherLenght: 1,
    stretcherDirection: 2,
    linkedHead: { x: -1, y: -1 },
    linkedFront: { x: -1, y: -1 },
    blobSubstitute: "RandomElement",
    overrideMalikDeathSpawnRows: false,
    malikDeathSpawnRowsOverride: [],
    overrideMalikDeathSpawnColumns: false,
    malikDeathSpawnColumnsOverride: [],
    overrideMalikThreatLife: false,
    malikThreatLifeOverride: 1
  };
}

export function getPieceIndex(board, collumn, line) {
  return line * board.collumn + collumn;
}

export function createBoard(collumn = 9, line = 9) {
  const pieces = [];
  for (let row = 0; row < line; row += 1) {
    for (let col = 0; col < collumn; col += 1) {
      pieces.push(createEmptyPiece(col, row));
    }
  }
  return { collumn, line, pieces };
}

function normalizePiece(rawPiece, collumn, line) {
  const normalized = createEmptyPiece(collumn, line);
  if (!rawPiece || typeof rawPiece !== "object") {
    return normalized;
  }

  normalized.type = normalizePieceType(rawPiece.type, normalized.type);
  normalized.backgroundType = normalizePieceType(
    rawPiece.backgroundType,
    normalized.backgroundType
  );
  normalized.foregroundType = normalizePieceType(
    rawPiece.foregroundType,
    normalized.foregroundType
  );
  normalized.parent = toVec2(rawPiece.parent, line, collumn);
  normalized.pieceColor = normalizePieceType(rawPiece.pieceColor, "Empty");
  normalized.startImune = toBool(rawPiece.startImune, false);
  normalized.isReceiver = toBool(rawPiece.isReceiver, false);
  normalized.linkedTeleporter = toVec2(rawPiece.linkedTeleporter, -1, -1);
  normalized.stretcherLenght = Math.max(1, toInt(rawPiece.stretcherLenght, 1));
  normalized.stretcherDirection = toInt(rawPiece.stretcherDirection, 2);
  normalized.linkedHead = toVec2(rawPiece.linkedHead, -1, -1);
  normalized.linkedFront = toVec2(rawPiece.linkedFront, -1, -1);
  normalized.blobSubstitute = normalizePieceType(
    rawPiece.blobSubstitute,
    "RandomElement"
  );
  normalized.overrideMalikDeathSpawnRows = toBool(
    rawPiece.overrideMalikDeathSpawnRows,
    false
  );
  normalized.malikDeathSpawnRowsOverride = Array.isArray(
    rawPiece.malikDeathSpawnRowsOverride
  )
    ? rawPiece.malikDeathSpawnRowsOverride.map((row) => toInt(row, 0))
    : [];
  normalized.overrideMalikDeathSpawnColumns = toBool(
    rawPiece.overrideMalikDeathSpawnColumns,
    false
  );
  normalized.malikDeathSpawnColumnsOverride = Array.isArray(
    rawPiece.malikDeathSpawnColumnsOverride
  )
    ? rawPiece.malikDeathSpawnColumnsOverride.map((col) => toInt(col, 0))
    : [];
  normalized.overrideMalikThreatLife = toBool(
    rawPiece.overrideMalikThreatLife,
    false
  );
  normalized.malikThreatLifeOverride = Math.max(
    1,
    toInt(rawPiece.malikThreatLifeOverride, 1)
  );

  const rawSpawner = rawPiece.spawnerData;
  if (rawSpawner && typeof rawSpawner === "object") {
    normalized.spawnerData = {
      spawnablePieces: normalizePieceList(rawSpawner.spawnablePieces),
      sequentialPieces: normalizePieceList(rawSpawner.sequentialPieces),
      quantity: Math.max(0, toInt(rawSpawner.quantity, 0))
    };
  }

  if (
    (normalized.backgroundType !== "Empty" ||
      normalized.foregroundType !== "Empty") &&
    normalized.type === "Empty"
  ) {
    normalized.type = "NoElement";
  }

  return normalized;
}

export function normalizeBoard(rawBoard) {
  const collumn = Math.max(
    1,
    toInt(rawBoard?.collumn ?? rawBoard?.column ?? rawBoard?.columns, 9)
  );
  const line = Math.max(
    1,
    toInt(rawBoard?.line ?? rawBoard?.row ?? rawBoard?.rows, 9)
  );

  const defaultBoard = createBoard(collumn, line);
  if (!Array.isArray(rawBoard?.pieces)) {
    return defaultBoard;
  }

  const byPosition = new Map();
  rawBoard.pieces.forEach((piece) => {
    const col = toInt(piece?.collumn, -1);
    const row = toInt(piece?.line, -1);
    if (col < 0 || row < 0 || col >= collumn || row >= line) return;
    byPosition.set(`${col}:${row}`, piece);
  });

  const pieces = [];
  for (let row = 0; row < line; row += 1) {
    for (let col = 0; col < collumn; col += 1) {
      const rawPiece = byPosition.get(`${col}:${row}`);
      pieces.push(normalizePiece(rawPiece, col, row));
    }
  }

  return {
    collumn,
    line,
    pieces
  };
}

export function resizeBoard(board, nextCollumn, nextLine) {
  const collumn = Math.max(1, toInt(nextCollumn, board.collumn));
  const line = Math.max(1, toInt(nextLine, board.line));
  const resized = createBoard(collumn, line);

  const limitRow = Math.min(board.line, line);
  const limitCol = Math.min(board.collumn, collumn);

  for (let row = 0; row < limitRow; row += 1) {
    for (let col = 0; col < limitCol; col += 1) {
      const oldIndex = getPieceIndex(board, col, row);
      const newIndex = getPieceIndex(resized, col, row);
      resized.pieces[newIndex] = structuredClone(board.pieces[oldIndex]);
      resized.pieces[newIndex].collumn = col;
      resized.pieces[newIndex].line = row;
    }
  }

  return resized;
}

function normalizeGameplayType(rawType) {
  if (rawType === 1 || rawType === "1") return "MalikFight";
  if (rawType === 0 || rawType === "0") return "ThreatClear";
  return GAMEPLAY_TYPES.includes(rawType) ? rawType : "ThreatClear";
}

function normalizeLevelId(rawId, fallbackIndex = 0) {
  if (typeof rawId === "string" && rawId.trim().length > 0) {
    return rawId.trim();
  }
  if (typeof rawId === "number" && Number.isFinite(rawId)) {
    return String(rawId);
  }
  return `level_${fallbackIndex}`;
}

export function normalizeLevel(rawLevel, fallbackIndex = 0) {
  const id = normalizeLevelId(
    rawLevel?.id ?? rawLevel?.name ?? rawLevel?.levelId,
    fallbackIndex
  );
  const board = normalizeBoard(rawLevel?.board);
  const malikBoardPhases = Array.isArray(rawLevel?.malikBoardPhases)
    ? rawLevel.malikBoardPhases.map((phase) => normalizeBoard(phase))
    : [];

  const basicPieces = normalizePieceList(rawLevel?.basicPieces);

  return {
    schemaVersion: LEVEL_SCHEMA_VERSION,
    id,
    maxMove: Math.max(1, toInt(rawLevel?.maxMove, 30)),
    initialWishProgressValue: toFloat(rawLevel?.initialWishProgressValue, 0),
    gameplayType: normalizeGameplayType(rawLevel?.gameplayType ?? rawLevel?.type),
    randomizable: toBool(rawLevel?.randomizable, false),
    difficulty:
      typeof rawLevel?.difficulty === "string" && rawLevel.difficulty
        ? rawLevel.difficulty
        : "Normal",
    basicPieces: basicPieces.length > 0 ? basicPieces : [...DEFAULT_BASIC_PIECES],
    board,
    isTutorial: toBool(rawLevel?.isTutorial, false),
    blockPowerUp: toBool(rawLevel?.blockPowerUp, false),
    tutorialType: toInt(rawLevel?.tutorialType, 0),
    threatType: normalizePieceTypeOrId(rawLevel?.threatType, "Empty"),
    delayBeforeStartTutorial: toFloat(rawLevel?.delayBeforeStartTutorial, 0),
    hideChristopherOnStartLevelWindow: toBool(
      rawLevel?.hideChristopherOnStartLevelWindow,
      false
    ),
    piecesPrefabs: normalizePrefabReferences(rawLevel?.piecesPrefabs),
    skipEndGameAnimations: toBool(rawLevel?.skipEndGameAnimations, false),
    malikBoardPhases,
    malikBoardThemeId:
      typeof rawLevel?.malikBoardThemeId === "string"
        ? rawLevel.malikBoardThemeId
        : "",
    skipMalikPlayButton: toBool(rawLevel?.skipMalikPlayButton, false),
    malikSkipVsAnimation: toBool(rawLevel?.malikSkipVsAnimation, false),
    lockFirstWishToTarget: toBool(rawLevel?.lockFirstWishToTarget, false),
    firstWishForcedTargetsByColor: normalizeFirstWishForcedTargetsByColor(
      rawLevel?.firstWishForcedTargetsByColor
    ),
    firstWishForcedTargets: normalizeFirstWishForcedTargets(
      rawLevel?.firstWishForcedTargets
    ),
    forcedMoveSequence: normalizeForcedMoveSequence(rawLevel?.forcedMoveSequence),
    enableGenieColorPieceSwaps: toBool(rawLevel?.enableGenieColorPieceSwaps, false),
    genieColorPieceSwaps: normalizeGenieColorPieceSwaps(rawLevel?.genieColorPieceSwaps)
  };
}

export function parseImportedPayload(payload) {
  if (!payload || typeof payload !== "object") {
    throw new Error("Invalid JSON payload.");
  }

  if (Array.isArray(payload)) {
    if (payload.length === 0) throw new Error("The JSON array is empty.");
    return payload.map((level, idx) => normalizeLevel(level, idx));
  }

  if (Array.isArray(payload.levels)) {
    if (payload.levels.length === 0) throw new Error("No levels in bundle.");
    return payload.levels.map((level, idx) => normalizeLevel(level, idx));
  }

  if (payload.board) {
    return [normalizeLevel(payload, 0)];
  }

  throw new Error("JSON must be a level object, a levels array, or a bundle.");
}

export function buildLevelBundle(levels) {
  return {
    schemaVersion: LEVEL_BUNDLE_SCHEMA_VERSION,
    exportedAt: new Date().toISOString(),
    levels: levels.map((level, idx) => normalizeLevel(level, idx))
  };
}

export function buildSingleLevelExport(level) {
  return normalizeLevel(level, 0);
}

export function toPrettyJson(value) {
  return JSON.stringify(value, null, 2);
}

export function sanitizeCsvPieces(value) {
  if (!value || typeof value !== "string") {
    return [...DEFAULT_BASIC_PIECES];
  }
  const unique = [];
  const seen = new Set();
  value
    .split(",")
    .map((token) => token.trim())
    .filter(Boolean)
    .forEach((token) => {
      const normalized = normalizePieceType(token, "");
      if (!normalized) return;
      if (seen.has(normalized)) return;
      seen.add(normalized);
      unique.push(normalized);
    });
  return unique.length > 0 ? unique : [...DEFAULT_BASIC_PIECES];
}
